import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Alert, Card, CardContent } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../api/store';
import { useUpdateUserMutation } from '../../api/userApi';
import { useToast } from '../../hooks/useToast';
import { hashPassword, simpleHash } from '../../api/authApi';
import { useNavigate } from 'react-router-dom';

export const ChangePasswordContainer: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const navigate = useNavigate();
  
  const user = useSelector((state: RootState) => state.auth.user);
  const [updateUser] = useUpdateUserMutation();
  const { showSuccess, showError } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!user) {
      setErrorMessage('You must be logged in to change your password');
      return;
    }

    if (!user.password || simpleHash(currentPassword) !== user.password) {
      setErrorMessage('Current password is incorrect');
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setErrorMessage('Password must be at least 6 characters');
      return;
    }

    try {
      // Hash and update the password
      const hashedPassword = hashPassword(newPassword);

      await updateUser({
        id: user.id,
        data: { password: hashedPassword }
      }).unwrap();

      showSuccess('Password changed successfully!');
      setSuccessMessage('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      navigate('/');
    } catch (error: any) {
      showError('Failed to change password');
      setErrorMessage(error.message || 'Failed to change password');
    }
  };

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
            Change Password
          </Typography>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {successMessage}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Current Password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              sx={{ mb: 3 }}
              helperText="Minimum 6 characters"
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={!currentPassword || !newPassword || !confirmPassword}
            >
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

